import React, { useState, useEffect, Component } from 'react';

// ErrorBoundary pour empêcher l'écran blanc en cas de crash d'un composant
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error) { console.log('Crash composant:', error); }
  render() {
    if (this.state.hasError) {
      return React.createElement('div', { style: { padding: 40, textAlign: 'center' } },
        React.createElement('div', { style: { fontSize: 32, marginBottom: 12 } }, '⚠️'),
        React.createElement('div', { style: { fontSize: 14, color: '#666', marginBottom: 16 } }, 'Une erreur est survenue'),
        React.createElement('button', {
          onClick: () => { this.setState({ hasError: false }); if (this.props.onReset) this.props.onReset(); },
          style: { padding: '10px 24px', border: 'none', borderRadius: 8, background: '#1C2833', color: '#fff', fontSize: 13, cursor: 'pointer' }
        }, '← Retour à l\'accueil')
      );
    }
    return this.props.children;
  }
}
import { collection, query, where, getDocs, orderBy, addDoc, serverTimestamp, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import FormulaireVisite from './FormulaireVisite';
import CarteClients from './CarteClients';
import PlanningSemaine from './PlanningSemaine';
import ProfilCommercial from './ProfilCommercial';
import SelecteurTheme from './SelecteurTheme';
import { useTheme } from '../ThemeContext';
import RechercheGlobale from './RechercheGlobale';
import FicheClient from './FicheClient';
import Notifications from './Notifications';
import { creerNotification } from './Notifications';
import { TrackingWidget } from './TrackingGPS';
import { useLang, LangToggle } from '../LanguageContext';
import { t } from '../i18n';

const COULEUR_DEFAULT = '#C0392B';

export default function CommercialHome({ user, onLogout }) {
  const { theme } = useTheme();
  const { lang, isRTL } = useLang();
  const [showTheme, setShowTheme] = useState(false);
  const [showRecherche, setShowRecherche] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [nbNotifs, setNbNotifs] = useState(0);
  const [ficheClient, setFicheClient] = useState(null);
  const [vue, setVue] = useState('accueil');
  const [widget, setWidget] = useState(null);
  const [visites, setVisites] = useState([]);
  const [clients, setClients] = useState([]);
  const [actions, setActions] = useState([]);
  const [wilaya, setWilaya] = useState('Constantine');
  const [clientSelectionne, setClientSelectionne] = useState(null);
  const [clientVisite, setClientVisite] = useState(null);
  const [actionExecutee, setActionExecutee] = useState(null);
  const [showFormVisite, setShowFormVisite] = useState(false);
  const [showFormClient, setShowFormClient] = useState(false);
  const [nouveauClient, setNouveauClient] = useState({ nom: '', telephone: '', commune: '', wilaya: 'Constantine', type: 'GROSSISTE', statut: 'Prospect', potentiel: 'Moyen', gpsText: '' });
  const [loadingClient, setLoadingClient] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [visiteASupprimer, setVisiteASupprimer] = useState(null);
  const [actionOuverte, setActionOuverte] = useState(null);
  const [filtreActions, setFiltreActions] = useState('toutes');
  const [showReporter, setShowReporter] = useState(false);
  const [nouvelleDateAction, setNouvelleDateAction] = useState('');
  const [commentaireCloture, setCommentaireCloture] = useState('');
  const [showConfirmExec, setShowConfirmExec] = useState(false);
  const [nbAffiche, setNbAffiche] = useState(15);

  useEffect(() => { chargerDonnees(); detecterWilaya(); chargerNbNotifs(); }, []);

  const chargerNbNotifs = async () => {
    try {
      const { getDocs, query, collection, where } = await import('firebase/firestore');
      const snap = await getDocs(query(collection(db, 'notifications'), where('destinataireId', '==', user.uid), where('lu', '==', false)));
      setNbNotifs(snap.size);
    } catch(e) {}
  };

  const chargerDonnees = async () => {
    try {
      const vSnap = await getDocs(query(collection(db, 'visites'), where('commercialId', '==', user.uid)));
      const vs = vSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      vs.sort((a, b) => new Date(b.heureDebut || 0) - new Date(a.heureDebut || 0));
      setVisites(vs);
      const cSnap = await getDocs(query(collection(db, 'clients'), where('commercialIds', 'array-contains', user.uid)));
      setClients(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const aSnap = await getDocs(query(collection(db, 'actions'), where('commercialId', '==', user.uid), where('statut', '==', 'A FAIRE')));
      setActions(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.log('Erreur:', e); }
  };

  const detecterWilaya = () => {
    navigator.geolocation?.getCurrentPosition(async pos => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`);
        const data = await res.json();
        setWilaya(data.address?.state || data.address?.city || 'Constantine');
      } catch { setWilaya('Constantine'); }
    });
  };

  const ajouterClient = async () => {
    if (!nouveauClient.nom.trim()) return;
    setLoadingClient(true);
    try {
      let gps = null;
      await new Promise(resolve => {
        navigator.geolocation?.getCurrentPosition(pos => { gps = { lat: pos.coords.latitude, lng: pos.coords.longitude }; resolve(); }, resolve);
      });
      const { gpsText, ...clientData } = nouveauClient;
      await addDoc(collection(db, 'clients'), {
        ...clientData,
        commercialIds: [user.uid],
        gps,
        createdAt: serverTimestamp(),
      });
      setShowFormClient(false);
      setNouveauClient({ nom: '', telephone: '', commune: '', wilaya: 'Constantine', type: 'GROSSISTE', statut: 'Prospect', potentiel: 'Moyen', gpsText: '' });
      chargerDonnees();
    } catch (e) { alert('Erreur: ' + e.message); }
    setLoadingClient(false);
  };

  const supprimerVisite = async (visiteId) => {
    try {
      await deleteDoc(doc(db, 'visites', visiteId));
      setVisiteASupprimer(null);
      chargerDonnees();
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const handleLogout = async () => { await signOut(auth); onLogout(); };

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' });
  const visitesToday = visites.filter(v => {
    if (!v.heureDebut) return false;
    return new Date(v.heureDebut).toDateString() === new Date().toDateString();
  });
  // Visites du mois en cours uniquement (le score ne doit pas cumuler tout l'historique)
  const debutMois = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const visitesMois = visites.filter(v => v.heureDebut && new Date(v.heureDebut) >= debutMois);
  const moisLabel = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const score = Math.round((visitesMois.length / Math.max(user.objectifVisites || 60, 1)) * 100);
  // Taux de réussite : % de visites du mois avec un résultat commercial positif
  const RESULTATS_POSITIFS = ['Commande passée', 'Devis demandé', 'Rendez-vous planifié', 'Promesse de commande'];
  const tauxReussite = visitesMois.length > 0
    ? Math.round((visitesMois.filter(v => RESULTATS_POSITIFS.includes(v.resultat)).length / visitesMois.length) * 100)
    : 0;
  const clientsFiltres = clients.filter(c => c.nom?.toLowerCase().includes(recherche.toLowerCase()));


  const aujourdhui = new Date().toISOString().split('T')[0];
  const actionsAujourdhui = actions.filter(a => a.dateAction === aujourdhui);
  const actionsRetard = actions.filter(a => a.dateAction && a.dateAction < aujourdhui);
  const actionsAvenir = actions.filter(a => a.dateAction && a.dateAction > aujourdhui);

  if (vue === 'carte') {
    return <ErrorBoundary onReset={() => setVue('accueil')}><CarteClients isManager={false} user={user} onClose={() => setVue('accueil')} /></ErrorBoundary>;
  }
  if (vue === 'planning') {
    return <ErrorBoundary onReset={() => setVue('accueil')}><PlanningSemaine isManager={false} user={user} onClose={() => setVue('accueil')} /></ErrorBoundary>;
  }
  if (showRecherche) return <RechercheGlobale user={user} isManager={false} onClose={() => setShowRecherche(false)} onSelectClient={(c) => { setShowRecherche(false); setFicheClient(c); }} />;

  if (showNotifs) return <Notifications user={user} isManager={false} onClose={() => { setShowNotifs(false); chargerNbNotifs(); }} />;
  if (ficheClient) return <FicheClient client={ficheClient} user={user} isManager={false} onClose={() => setFicheClient(null)} />;

  if (vue === 'profil') return <ProfilCommercial user={user} onLogout={onLogout} onClose={() => setVue('accueil')} />;

  if (showFormVisite && clientSelectionne) {
    return <FormulaireVisite user={user} client={clientSelectionne} onClose={() => { setShowFormVisite(false); setClientSelectionne(null); }} onSave={() => chargerDonnees()} />;
  }

  if (showFormVisite && clientVisite) {
    return <FormulaireVisite user={user} client={clientVisite} onClose={() => { setShowFormVisite(false); setClientVisite(null); setActionExecutee(null); }}
      onSave={async () => {
        if (actionExecutee) {
          try {
            await updateDoc(doc(db, 'actions', actionExecutee.id), {
              statut: 'FAIT',
              commentaireCloture: 'Visite effectuée',
              dateCloture: new Date().toISOString(),
              heureExecution: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
            });
          } catch (e) { console.log(e); }
        }
        setActionExecutee(null);
        setClientVisite(null);
        chargerDonnees();
      }} />;
  }

  if (widget === 'objectif') return (
    <div style={styles.app}>
      <div style={{ ...styles.topbar, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <button onClick={() => setWidget(null)} style={styles.backBtn}>←</button>
        <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Objectif du mois</span>
      </div>
      <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[
          { label: 'Visites réalisées', val: `${visitesMois.length} / ${user.objectifVisites || 60}`, pct: score },
          { label: 'Taux de réussite', val: `${tauxReussite}%`, pct: tauxReussite },
          { label: 'Nouveaux clients', val: `${clients.filter(c => c.statut === 'Nouveau client').length} / ${user.objectifClients || 3}`, pct: Math.round(clients.filter(c => c.statut === 'Nouveau client').length / (user.objectifClients || 3) * 100) },
          { label: 'Score global', val: `${score}%`, pct: score },
        ].map((k, i) => (
          <div key={i} style={styles.kpiCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: '#666' }}>{k.label}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: k.pct >= 90 ? '#3B6D11' : k.pct >= 60 ? '#854F0B' : theme.couleur }}>{k.val}</span>
            </div>
            <div style={{ height: 5, background: '#f0f0f0', borderRadius: 99 }}>
              <div style={{ height: 5, background: k.pct >= 90 ? '#3B6D11' : k.pct >= 60 ? '#E67E22' : theme.couleur, borderRadius: 99, width: `${Math.min(k.pct, 100)}%` }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const actionsFiltrees = (filtreActions === 'retard' ? actionsRetard
    : filtreActions === 'aujourdhui' ? actionsAujourdhui
    : filtreActions === 'avenir' ? actionsAvenir
    : [...actionsRetard, ...actionsAujourdhui, ...actionsAvenir])
    .sort((a, b) => (a.dateAction || '').localeCompare(b.dateAction || ''));

  const actionsVisibles = actionsFiltrees.slice(0, nbAffiche);

  const executerAction = async (action) => {
    try {
      await updateDoc(doc(db, 'actions', action.id), {
        statut: 'FAIT',
        commentaireCloture: commentaireCloture.trim() || 'Exécutée',
        dateCloture: new Date().toISOString(),
        heureExecution: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      });
      setActionOuverte(null);
      setCommentaireCloture('');
      setShowConfirmExec(false);
      // Recharger les actions
      const aSnap = await getDocs(query(collection(db, 'actions'), where('commercialId', '==', user.uid), where('statut', '==', 'A FAIRE')));
      setActions(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const reporterAction = async (action) => {
    if (!nouvelleDateAction) { alert('Choisissez une nouvelle date'); return; }
    try {
      await updateDoc(doc(db, 'actions', action.id), {
        statut: 'ANNULE',
        commentaireCloture: 'Reportée au ' + nouvelleDateAction,
        dateCloture: new Date().toISOString(),
      });
      await addDoc(collection(db, 'actions'), {
        commercialId: action.commercialId,
        clientId: action.clientId,
        clientNom: action.clientNom,
        action: action.action,
        dateAction: nouvelleDateAction,
        responsable: action.responsable || 'Commercial',
        rappels: action.rappels || [],
        rappelsDates: [],
        statut: 'A FAIRE',
        createdAt: serverTimestamp(),
        reporteeDepuis: action.id,
      });
      setActionOuverte(null);
      setShowReporter(false);
      setNouvelleDateAction('');
      const aSnap = await getDocs(query(collection(db, 'actions'), where('commercialId', '==', user.uid), where('statut', '==', 'A FAIRE')));
      setActions(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const getStatutJour = (dateAction) => {
    if (dateAction < aujourdhui) return { label: 'En retard', bg: '#FCEBEB', color: '#A32D2D', icon: '🔴' };
    if (dateAction === aujourdhui) return { label: "Aujourd'hui", bg: '#FFF8E8', color: '#854F0B', icon: '🟠' };
    return { label: 'À venir', bg: '#EAF3DE', color: '#3B6D11', icon: '🟢' };
  };

  const joursRetard = (dateAction) => {
    const diff = Math.floor((new Date(aujourdhui) - new Date(dateAction)) / 86400000);
    return diff > 0 ? diff : 0;
  };

  if (widget === 'actions') return (
    <div style={styles.app}>
      <div style={{ ...styles.topbar, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <button onClick={() => { setWidget(null); setActionOuverte(null); setShowReporter(false); setShowConfirmExec(false); }} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Centre d'actions</span>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>{actions.length} action(s) · {actionsRetard.length} en retard</div>
        </div>
      </div>

      {/* FILTRES */}
      <div style={{ display: 'flex', gap: 6, padding: '10px 12px', overflowX: 'auto' }}>
        {[
          { key: 'toutes', label: `Toutes (${actions.length})` },
          { key: 'retard', label: `🔴 Retard (${actionsRetard.length})` },
          { key: 'aujourdhui', label: `🟠 Aujourd'hui (${actionsAujourdhui.length})` },
          { key: 'avenir', label: `🟢 À venir (${actionsAvenir.length})` },
        ].map(f => (
          <button key={f.key}
            onClick={() => { setFiltreActions(f.key); setNbAffiche(15); setActionOuverte(null); }}
            style={{ padding: '6px 12px', border: filtreActions === f.key ? '1px solid #1C2833' : '1px solid #ddd', borderRadius: 99, background: filtreActions === f.key ? '#1C2833' : '#fff', color: filtreActions === f.key ? '#fff' : '#666', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* LISTE DES ACTIONS */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {actionsFiltrees.length === 0 && (
          <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>
            {filtreActions === 'toutes' ? 'Aucune action prévue' : 'Aucune action dans cette catégorie'}
          </div>
        )}

        {actionsVisibles.map(a => {
          const st = getStatutJour(a.dateAction);
          const ouverte = actionOuverte === a.id;
          const retardJours = joursRetard(a.dateAction);
          const nomClient = a.clientNom || 'Client sans nom';
          const typeAction = a.action || 'Action non définie';
          return (
            <div key={a.id} style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderLeft: `4px solid ${st.color}` }}>
              <div onClick={() => { setActionOuverte(ouverte ? null : a.id); setShowReporter(false); setShowConfirmExec(false); setCommentaireCloture(''); }}
                style={{ padding: 12, cursor: 'pointer' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1C2833', marginBottom: 3 }}>{nomClient}</div>
                <div style={{ fontSize: 12, color: '#555', marginBottom: 3 }}>{typeAction}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: '#888' }}>
                    📅 {a.dateAction ? new Date(a.dateAction + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : 'Date non définie'}
                    {a.responsable ? ` · 👤 ${a.responsable}` : ''}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 99, background: st.bg, color: st.color }}>
                    {st.label}{retardJours > 0 ? ` J+${retardJours}` : ''}
                  </span>
                </div>
              </div>

              {/* PANNEAU D'ACTION DÉPLIÉ */}
              {ouverte && !showConfirmExec && !showReporter && (
                <div style={{ padding: '0 12px 12px', borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 10 }}>
                  {/* Bouton Exécuter */}
                  <button onClick={() => setShowConfirmExec(true)}
                    style={{ width: '100%', padding: '12px', border: 'none', borderRadius: 8, background: '#3B6D11', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    ✅ Exécuter cette action
                  </button>
                  {/* Bouton Saisir une visite */}
                  <button onClick={() => {
                    const cl = clients.find(c => c.id === a.clientId);
                    if (cl) { setClientVisite(cl); setShowFormVisite(true); setActionExecutee(a); }
                    else alert('Client introuvable');
                  }}
                    style={{ width: '100%', padding: '12px', border: 'none', borderRadius: 8, background: '#185FA5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    📝 Saisir une visite pour ce client
                  </button>
                  {/* Bouton Reporter */}
                  <button onClick={() => setShowReporter(true)}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: 8, background: '#fff', color: '#854F0B', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    📆 Reporter à une autre date
                  </button>
                  {/* Bouton Appeler */}
                  {(() => { const cl = clients.find(c => c.id === a.clientId); return cl?.telephone ? (
                    <button onClick={() => window.location.href = `tel:${cl.telephone}`}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: 8, background: '#fff', color: '#1C2833', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      📞 Appeler {cl.telephone}
                    </button>
                  ) : null; })()}
                </div>
              )}

              {/* FORMULAIRE EXÉCUTION */}
              {ouverte && showConfirmExec && (
                <div style={{ padding: '0 12px 12px', borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#3B6D11', marginBottom: 6 }}>Commentaire d'exécution :</div>
                  <textarea value={commentaireCloture} onChange={e => setCommentaireCloture(e.target.value)}
                    placeholder="Résultat de l'action (ex: Client rappelé, RDV confirmé pour lundi...)"
                    style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 12, minHeight: 60, fontFamily: 'system-ui', resize: 'vertical', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button onClick={() => setShowConfirmExec(false)}
                      style={{ flex: 1, padding: 10, border: '1px solid #ddd', borderRadius: 8, background: '#fff', color: '#666', fontSize: 12, cursor: 'pointer' }}>Annuler</button>
                    <button onClick={() => executerAction(a)}
                      style={{ flex: 1, padding: 10, border: 'none', borderRadius: 8, background: '#3B6D11', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      ✅ Confirmer l'exécution
                    </button>
                  </div>
                </div>
              )}

              {/* FORMULAIRE REPORT */}
              {ouverte && showReporter && (
                <div style={{ padding: '0 12px 12px', borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#854F0B', marginBottom: 6 }}>Reporter au :</div>
                  <input type="date" value={nouvelleDateAction} onChange={e => setNouvelleDateAction(e.target.value)}
                    min={aujourdhui}
                    style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button onClick={() => setShowReporter(false)}
                      style={{ flex: 1, padding: 10, border: '1px solid #ddd', borderRadius: 8, background: '#fff', color: '#666', fontSize: 12, cursor: 'pointer' }}>Annuler</button>
                    <button onClick={() => reporterAction(a)}
                      style={{ flex: 1, padding: 10, border: 'none', borderRadius: 8, background: '#854F0B', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      📆 Confirmer le report
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Bouton Voir plus */}
        {nbAffiche < actionsFiltrees.length && (
          <button onClick={() => setNbAffiche(n => n + 15)}
            style={{ width: '100%', padding: 12, border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#1C2833', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
            Voir plus ({actionsFiltrees.length - nbAffiche} restantes)
          </button>
        )}
        {actionsFiltrees.length > 0 && (
          <div style={{ textAlign: 'center', fontSize: 10, color: '#aaa', paddingTop: 4 }}>
            {Math.min(nbAffiche, actionsFiltrees.length)} / {actionsFiltrees.length} affichées
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={styles.app}>
      {/* TOPBAR */}
      <div style={{ ...styles.topbar, background: theme.couleur }}>
        <div>
          <div style={styles.topbarName}>Bonjour, {user.prenom || user.nom}</div>
          <div style={styles.topbarSub}>{today} · 📍 {wilaya}</div>
        </div>
        <button style={styles.themeBtn} onClick={() => setShowRecherche(true)}>🔍</button>
        <button style={styles.themeBtn} onClick={() => setShowNotifs(true)}>
          🔔{nbNotifs > 0 && <span style={styles.notifBadge}>{nbNotifs}</span>}
        </button>
        <button style={styles.themeBtn} onClick={() => setShowTheme(true)}>🎨</button>
        <div style={{...styles.avatar, cursor: 'pointer'}} onClick={() => setVue('profil')}>{(user.prenom || user.nom || 'U')[0].toUpperCase()}</div>
      </div>

      {/* CONTENU PAR VUE */}
      {vue === 'accueil' && (
        <div style={styles.widgets}>
          <div style={styles.widgetCard} onClick={() => setWidget('objectif')}>
            <div style={styles.widgetRow}>
              <div><div style={styles.widgetTitle}>Objectif du mois</div><div style={styles.widgetSub}>{moisLabel}</div></div>
              <div style={{ fontSize: 22, fontWeight: 700, color: theme.couleur }}>{score}%</div>
            </div>
            <div style={styles.progressTrack}><div style={{ ...styles.progressFill, width: `${Math.min(score, 100)}%` }}></div></div>
          </div>

          <div style={styles.widgetCard} onClick={() => setVue('clients')}>
            <div style={styles.widgetRow}>
              <div><div style={styles.widgetTitle}>Planning du jour</div><div style={styles.widgetSub}>{visitesToday.length} visite(s) réalisée(s)</div></div>
              <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 99, background: '#EAF3DE', color: '#3B6D11' }}>{visitesToday.length} faite(s)</span>
            </div>
            <div style={{ marginTop: 10 }}>
              {clients.slice(0, 3).map((c, i) => (
                <div key={i} style={styles.visitPreviewRow}><span>{c.nom}</span><span style={{ color: '#854F0B', fontSize: 11 }}>→ Visiter</span></div>
              ))}
              {clients.length === 0 && <div style={{ fontSize: 12, color: '#aaa' }}>Aucun client — Ajoutez-en un !</div>}
            </div>
          </div>

          <div style={styles.widgetCard} onClick={() => setWidget('actions')}>
            <div style={styles.widgetRow}>
              <div><div style={styles.widgetTitle}>Centre d'actions</div><div style={styles.widgetSub}>{actions.length} action(s) à traiter</div></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {actionsRetard.length > 0 && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#FCEBEB', color: '#A32D2D', fontWeight: 600 }}>🔴 {actionsRetard.length} en retard</span>}
                {actionsAujourdhui.length > 0 && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#FFF8E8', color: '#854F0B', fontWeight: 600 }}>🟠 {actionsAujourdhui.length} aujourd'hui</span>}
                {actionsAvenir.length > 0 && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#EAF3DE', color: '#3B6D11' }}>{actionsAvenir.length} à venir</span>}
              </div>
            </div>
          </div>



        </div>
      )}

      {vue === 'clients' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '10px 12px', background: '#fff', borderBottom: '1px solid #eee', display: 'flex', gap: 8 }}>
            <input style={{ ...styles.searchInput, flex: 1 }} placeholder="🔍 Rechercher un client..." value={recherche} onChange={e => setRecherche(e.target.value)} />
            <button style={{ padding: '9px 14px', background: COULEUR_DEFAULT, border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }} onClick={() => setShowFormClient(true)}>+ Client</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {clientsFiltres.map((c, i) => (
              <div key={i} style={styles.clientCard}>
                <div style={styles.clientHeader}>
                  <div style={styles.clientAv}>{c.nom?.[0]?.toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div style={styles.visitName}>{c.nom}</div>
                    <div style={styles.visitSub}>{c.type || c.statut} · {c.commune}</div>
                  </div>
                  <button style={{ ...styles.saisirBtn, background: '#E6F1FB', color: '#185FA5', marginRight: 4 }} onClick={() => setFicheClient(c)}>👁</button>
                  <button style={styles.saisirBtn} onClick={() => { setClientSelectionne(c); setShowFormVisite(true); }}>
                    ✏️ Saisir
                  </button>
                </div>
                {c.telephone && <div style={{ fontSize: 12, color: '#888', padding: '4px 0 0 42px' }}>📞 {c.telephone}</div>}
              </div>
            ))}
            {clientsFiltres.length === 0 && (
              <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>
                <div style={{ fontSize: 32 }}>👥</div>
                <div style={{ marginTop: 8 }}>Aucun client trouvé</div>
              </div>
            )}
          </div>

        </div>
      )}

      {vue === 'visites' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', marginBottom: 8 }}>Historique des visites ({visites.length})</div>
          {visites.length === 0 && <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>Aucune visite enregistrée</div>}
          {visites.map((v, i) => (
            <div key={i} style={{ ...styles.clientCard, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={styles.visitName}>{v.clientNom}</div>
                  <div style={styles.visitSub}>{v.resultat} · {v.heureDebut ? new Date(v.heureDebut).toLocaleDateString('fr-FR') : ''}</div>
                  {v.commentaire && <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>💬 {v.commentaire}</div>}
                  {(v.vocalData || v.vocalUrl) && <audio src={v.vocalData || v.vocalUrl} controls style={{ width: '100%', height: 32, marginTop: 6 }} />}
                </div>
                <button style={{ background: '#FCEBEB', border: 'none', borderRadius: 6, color: '#A32D2D', fontSize: 11, cursor: 'pointer', padding: '4px 8px', flexShrink: 0 }}
                  onClick={() => setVisiteASupprimer(v)}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FORM NOUVEAU CLIENT */}
      {showFormClient && (
        <div style={styles.formOverlay}>
          <div style={styles.formModal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>Nouveau client</div>
              <button onClick={() => setShowFormClient(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            {[
              { label: 'Nom *', key: 'nom', placeholder: 'Nom du client ou entreprise' },
              { label: 'Telephone', key: 'telephone', placeholder: '0X XX XX XX XX' },
              { label: 'Commune', key: 'commune', placeholder: 'Ex: Constantine' },
              { label: 'Wilaya', key: 'wilaya', placeholder: 'Ex: Constantine' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{f.label}</div>
                <input style={styles.formInput} placeholder={f.placeholder} value={nouveauClient[f.key]}
                  onChange={e => setNouveauClient(n => ({ ...n, [f.key]: e.target.value }))} />
              </div>
            ))}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Type</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['GROSSISTE', 'DEMI GROS', 'DETAILLANT', 'DECORATIVE', 'ENTREPRENEUR'].map(tp => (
                  <button key={tp} style={{ ...styles.choiceSmall, ...(nouveauClient.type === tp ? styles.choiceSmallActive : {}) }}
                    onClick={() => setNouveauClient(n => ({ ...n, type: tp }))}>{tp}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Statut</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['Prospect', 'Client actif', 'Nouveau client', 'Client inactif'].map(s => (
                  <button key={s} style={{ ...styles.choiceSmall, ...(nouveauClient.statut === s ? styles.choiceSmallActive : {}) }}
                    onClick={() => setNouveauClient(n => ({ ...n, statut: s }))}>{s}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Potentiel</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {['Faible', 'Moyen', 'Fort'].map(p => (
                  <button key={p} style={{ ...styles.choiceSmall, ...(nouveauClient.potentiel === p ? styles.choiceSmallActive : {}) }}
                    onClick={() => setNouveauClient(n => ({ ...n, potentiel: p }))}>{p}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Position GPS (optionnel)</div>
              <input style={styles.formInput} placeholder="Ex: 36.3650, 6.6147"
                value={nouveauClient.gpsText}
                onChange={e => setNouveauClient(n => ({ ...n, gpsText: e.target.value }))} />
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>Clic droit sur Google Maps puis Copier les coordonnees</div>
            </div>
            <button style={{ ...styles.addClientBtn, opacity: nouveauClient.nom ? 1 : 0.4 }}
              onClick={ajouterClient} disabled={loadingClient || !nouveauClient.nom}>
              {loadingClient ? 'Enregistrement...' : '✅ Ajouter le client'}
            </button>
          </div>
        </div>
      )}

      {visiteASupprimer && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 480, margin: '0 auto', borderRadius: '20px 20px 0 0', padding: '20px 16px 30px' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1C2833', marginBottom: 10 }}>🗑 Supprimer cette visite ?</div>
            <div style={{ fontSize: 13, color: '#444', marginBottom: 6 }}>Client : <strong>{visiteASupprimer.clientNom}</strong></div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>{visiteASupprimer.heureDebut ? new Date(visiteASupprimer.heureDebut).toLocaleDateString('fr-FR') : ''} · {visiteASupprimer.resultat}</div>
            <div style={{ fontSize: 12, color: '#A32D2D', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginBottom: 16 }}>⚠️ Action irréversible</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ flex: 1, padding: 11, border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 14, cursor: 'pointer' }} onClick={() => setVisiteASupprimer(null)}>Annuler</button>
              <button style={{ flex: 2, padding: 11, background: '#C0392B', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }} onClick={() => supprimerVisite(visiteASupprimer.id)}>🗑 Confirmer</button>
            </div>
          </div>
        </div>
      )}
      {showTheme && <SelecteurTheme onClose={() => setShowTheme(false)} />}

      {/* NAVBAR */}
      <div style={styles.navbar}>
        {[
          { id: 'accueil', icon: '🏠', label: 'Accueil' },
          { id: 'clients', icon: '👥', label: 'Clients' },
          { id: 'visites', icon: '📋', label: 'Visites' },
          { id: 'carte', icon: '🗺️', label: 'Carte', action: () => setVue('carte') },
          { id: 'planning', icon: '📅', label: 'Planning', action: () => setVue('planning') },
        ].map(nav => (
          <div key={nav.id} style={{ ...styles.navItem, color: vue === nav.id ? theme.couleur : '#999' }} onClick={() => { setWidget(null); setVue(nav.id); }}>
            <span style={{ fontSize: 20 }}>{nav.icon}</span>
            <span style={{ fontSize: 10 }}>{nav.label}</span>
          </div>
        ))}

      </div>
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', height: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif', overflow: 'hidden' },
  topbar: { background: COULEUR_DEFAULT, padding: '1rem 1.25rem', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  topbarName: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 12, opacity: 0.8, marginTop: 2 },
  avatar: { width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#fff' },
  backBtn: { background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer' },
  widgets: { flex: 1, display: 'flex', flexDirection: 'column', gap: 8, padding: 8 },
  widgetCard: { background: '#fff', borderRadius: 12, padding: '1rem 1.25rem', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  widgetRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  widgetTitle: { fontSize: 14, fontWeight: 600, color: '#1C2833' },
  widgetSub: { fontSize: 12, color: '#888', marginTop: 3 },
  progressTrack: { height: 5, background: '#f0f0f0', borderRadius: 99, marginTop: 8 },
  progressFill: { height: 5, background: COULEUR_DEFAULT, borderRadius: 99, transition: 'width 0.5s' },
  visitPreviewRow: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666', marginTop: 5 },
  kpiCard: { background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  actionCard: { background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  searchInput: { width: '100%', padding: '9px 12px', border: '1px solid #eee', borderRadius: 8, fontSize: 14, background: '#f9f9f9', fontFamily: 'system-ui' },
  clientCard: { background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  clientHeader: { display: 'flex', alignItems: 'center', gap: 10 },
  clientAv: { width: 36, height: 36, borderRadius: '50%', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: COULEUR_DEFAULT, flexShrink: 0 },
  visitName: { fontSize: 13, fontWeight: 600, color: '#1C2833' },
  visitSub: { fontSize: 11, color: '#888', marginTop: 2 },
  saisirBtn: { padding: '7px 12px', background: COULEUR_DEFAULT, border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 },
  addClientBtn: { width: '100%', padding: 12, background: COULEUR_DEFAULT, border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  formOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', alignItems: 'flex-end' },
  formModal: { background: '#fff', width: '100%', maxWidth: 480, margin: '0 auto', borderRadius: '20px 20px 0 0', padding: '20px 16px 30px', maxHeight: '85vh', overflowY: 'auto' },
  formInput: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui' },
  choiceSmall: { padding: '6px 12px', border: '1px solid #ddd', borderRadius: 99, background: '#fff', color: '#666', fontSize: 12, cursor: 'pointer' },
  choiceSmallActive: { border: `1px solid ${COULEUR_DEFAULT}`, background: '#FCEBEB', color: COULEUR_DEFAULT, fontWeight: 600 },
  notifBadge: { position: 'absolute', top: -4, right: -4, background: '#E74C3C', color: '#fff', fontSize: 9, width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 },
  themeBtn: { background: 'rgba(255,255,255,0.2)', position: 'relative', border: 'none', borderRadius: 8, width: 32, height: 32, fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  navbar: { display: 'flex', background: '#fff', borderTop: '1px solid #eee', padding: '8px 0 4px' },
  navItem: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 10 },
};
